import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createReport } from "../../api";
import "./ReportForm.css";

const fakeUser = { id: 7, unit_id: 3 };
const NOTES_LIMIT = 500;

export default function ReportForm() {
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  // Presentational only -- flips true on a failed submit attempt so the
  // (unchanged) "title is required" rule can be shown inline instead of in
  // a browser alert(); the rule itself (title.trim() must be non-empty) is
  // identical to before.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const nav = useNavigate();

  const isDirty = !!(title || start || end || notes);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // Native browser "leave site?" prompt when there's unsaved input --
  // covers tab close / refresh / typed URL navigation. Purely client-side,
  // no new API, no change to what "dirty" means beyond "has the user typed
  // anything at all".
  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const dateRangeText = useMemo(() => {
    if (start && end) return `${start} to ${end}`;
    if (start) return `From ${start}`;
    if (end) return `Till ${end}`;
    return "Not selected";
  }, [start, end]);

  const tripDayCount = useMemo(() => {
    if (!start || !end) return null;
    const s = new Date(start);
    const e = new Date(end);
    const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : null;
  }, [start, end]);

  const titleError = submitAttempted && !title.trim() ? "Report title is required." : null;

  const resetForm = () => {
    setTitle("");
    setStart("");
    setEnd("");
    setNotes("");
    setSubmitAttempted(false);
  };

  const handleBackClick = (e) => {
    if (isDirty && !window.confirm("You have unsaved changes. Leave this page anyway?")) {
      e.preventDefault();
    }
  };

  const handleCancelClick = () => {
    if (isDirty && !window.confirm("You have unsaved changes. Leave this page anyway?")) {
      return;
    }
    nav("/admin/accounts/my-reports");
  };

  async function handleCreate() {
    setSubmitAttempted(true);

    if (!title.trim()) {
      return;
    }

    if (start && end && new Date(end) < new Date(start)) {
      alert("Trip end date cannot be before trip start date");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        title: title.trim(),
        requester_id: fakeUser.id,
        unit_id: fakeUser.unit_id,
        trip_start_date: start || null,
        trip_end_date: end || null,
        notes: notes.trim(),
      };

      const res = await createReport(payload);

      if (!res?.id) {
        alert("Create succeeded but no id returned");
        return;
      }

      nav(`/admin/accounts/reports/${res.id}`);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  const statCards = [
    {
      label: "Requester",
      value: `#${fakeUser.id}`,
      icon: "fa-solid fa-user",
      className: "report-stat-blue",
    },
    {
      label: "Unit",
      value: `#${fakeUser.unit_id}`,
      icon: "fa-solid fa-building",
      className: "report-stat-purple",
    },
    {
      label: "Trip Date",
      value: tripDayCount ? `${tripDayCount} day${tripDayCount === 1 ? "" : "s"}` : "Not selected",
      icon: "fa-solid fa-route",
      className: "report-stat-orange",
      valueClass: tripDayCount ? "" : "report-stat-muted-value",
    },
    {
      label: "Status",
      value: "Draft",
      icon: "fa-solid fa-circle-check",
      className: "report-stat-green",
      valueClass: "report-status-chip report-status-draft",
    },
  ];

  return (
    <div className="unit-page report-form-theme">
      {/* TOP HEADING */}
      <div className="report-heading-box">
        <div className="report-heading-content">
          <div className="report-heading-icon">
            <i className="fa-solid fa-file-invoice-dollar" aria-hidden="true"></i>
          </div>

          <div>
            <div className="unit-page-label">Accounts</div>
            <h2>Create Expense Report</h2>
            <p>Create and submit travel or expense details for approval.</p>
          </div>
        </div>

        <div className="unit-header-actions">
          <Link
            to="/admin/accounts/my-reports"
            className="unit-btn unit-btn-ghost text-decoration-none"
            onClick={handleBackClick}
          >
            <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
            Back
          </Link>

          <button
            type="button"
            className="unit-btn unit-btn-outline"
            onClick={resetForm}
          >
            <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
            Reset
          </button>

          <button
            type="button"
            className="unit-btn unit-btn-primary"
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                Creating...
              </>
            ) : (
              <>
                <i className="fa-solid fa-file-circle-plus" aria-hidden="true"></i>
                Create Report
              </>
            )}
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="unit-stats-row">
        {statCards.map((card) => (
          <div
            key={card.label}
            className={`unit-stat-card ${card.className}`}
          >
            <div className="report-stat-content">
              <div className="report-stat-copy">
                <span>{card.label}</span>
                <strong className={card.valueClass || ""}>
                  {card.value}
                </strong>
              </div>

              <div className="report-stat-icon">
                <i className={card.icon} aria-hidden="true"></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* STEP INDICATOR -- this page only ever handles step 1; Expense
          Items / Review happen on a separate page after the report is
          created and the app redirects there. Purely a visual "you are
          here" indicator, not a functional step switcher. */}
      <div className="report-steps">
        <div className="report-step report-step-active">
          <span className="report-step-num">01</span>
          <span className="report-step-label">Report Details</span>
        </div>
        <div className="report-step-divider"></div>
        <div className="report-step report-step-upcoming">
          <span className="report-step-num">02</span>
          <span className="report-step-label">Expense Items</span>
        </div>
        <div className="report-step-divider"></div>
        <div className="report-step report-step-upcoming">
          <span className="report-step-num">03</span>
          <span className="report-step-label">Review &amp; Submit</span>
        </div>
      </div>

      {/* MAIN FORM CARD */}
      <div className="unit-card">
        <div className="unit-toolbar">
          <div className="report-section-title">
            <div className="report-section-icon">
              <i className="fa-solid fa-file-lines" aria-hidden="true"></i>
            </div>

            <div>
              <h5>Report Details</h5>
              <p>Provide basic trip information before adding expenses.</p>
            </div>
          </div>
        </div>

        <div className="report-form-body">
          <div className="row g-3">
            <div className="col-12">
              <div className={`report-field-card report-field-card-primary${titleError ? " report-field-card-error" : ""}`}>
                <div className="report-field-icon">
                  <i className="fa-solid fa-heading" aria-hidden="true"></i>
                </div>

                <div className="report-field-content">
                  <label className="form-label" htmlFor="report-title">
                    Report Title <span className="text-danger">*</span>
                  </label>

                  <input
                    id="report-title"
                    className="form-control report-title-input"
                    placeholder="Example: Delhi Official Trip Expenses"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    aria-invalid={!!titleError}
                    aria-describedby={titleError ? "report-title-error" : "report-title-hint"}
                  />

                  {titleError ? (
                    <small id="report-title-error" className="report-field-error-text">
                      <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                      {titleError}
                    </small>
                  ) : (
                    <small id="report-title-hint">
                      <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
                      Use a clear title that identifies the trip or expense purpose.
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="report-field-card report-daterange-card">
                <div className="report-field-icon report-field-icon-date">
                  <i className="fa-regular fa-calendar" aria-hidden="true"></i>
                </div>

                <div className="report-field-content report-daterange-content">
                  <label className="form-label">Trip Duration</label>

                  <div className="report-daterange-inputs">
                    <div className="report-daterange-field">
                      <span className="report-daterange-field-label">Start Date</span>
                      <input
                        id="report-trip-start"
                        type="date"
                        className="form-control"
                        value={start}
                        onChange={(e) => setStart(e.target.value)}
                      />
                    </div>

                    <i className="fa-solid fa-arrow-right report-daterange-arrow" aria-hidden="true"></i>

                    <div className="report-daterange-field">
                      <span className="report-daterange-field-label">End Date</span>
                      <input
                        id="report-trip-end"
                        type="date"
                        className="form-control"
                        value={end}
                        onChange={(e) => setEnd(e.target.value)}
                      />
                    </div>
                  </div>

                  <small className="report-daterange-summary">
                    <i className="fa-solid fa-clock" aria-hidden="true"></i>
                    {start && end ? (
                      <>
                        {dateRangeText}
                        {tripDayCount && <span className="report-daycount-chip">{tripDayCount} day{tripDayCount === 1 ? "" : "s"}</span>}
                      </>
                    ) : (
                      "Select a start and end date to see the trip duration."
                    )}
                  </small>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="report-field-card">
                <div className="report-field-icon report-field-icon-notes">
                  <i className="fa-regular fa-note-sticky" aria-hidden="true"></i>
                </div>

                <div className="report-field-content">
                  <div className="report-notes-label-row">
                    <label className="form-label" htmlFor="report-notes">
                      Notes
                    </label>
                    <span className={`report-char-counter${notes.length > NOTES_LIMIT ? " report-char-counter-over" : ""}`}>
                      {notes.length} / {NOTES_LIMIT}
                    </span>
                  </div>

                  <textarea
                    id="report-notes"
                    rows={4}
                    className="form-control report-notes-textarea"
                    placeholder="Write purpose, travel details, or any additional information..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />

                  <small>
                    <i className="fa-solid fa-pen" aria-hidden="true"></i>
                    Add travel purpose, approval details or additional notes.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EXPENSE PREVIEW */}
        <div className="report-preview-section">
          <div className="report-preview-header">
            <i className="fa-solid fa-receipt" aria-hidden="true"></i>
            Expense Summary
          </div>
          <div className="report-preview-empty">
            <div className="report-preview-empty-icon">
              <i className="fa-solid fa-file-circle-plus" aria-hidden="true"></i>
            </div>
            <p className="report-preview-empty-title">No expenses added</p>
            <p className="report-preview-empty-desc">After creating this report, you can add expense items to it.</p>
          </div>
        </div>
      </div>

      {/* STICKY FOOTER */}
      <div className="report-sticky-footer">
        <div className="report-footer-info">
          <div className="report-footer-icon">
            <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
          </div>
          <div className="unit-count-text">
            After creating this report you can add expense items.
          </div>
        </div>

        <div className="unit-actions">
          <button
            type="button"
            className="unit-btn unit-btn-ghost"
            onClick={handleCancelClick}
          >
            Cancel
          </button>

          <button
            type="button"
            className="unit-btn unit-btn-primary"
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                Creating...
              </>
            ) : (
              <>
                <i className="fa-solid fa-file-circle-plus" aria-hidden="true"></i>
                Create Report
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
